import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

import { Table, usePagination } from '@frontend/features/common/components/Table';
import { Link } from '@frontend/features/common/components/Link';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';

import { useGetOrganizationsByAgency } from '@frontend/features/organizations/queries';
import { CreateOrganizationModal } from '@frontend/features/organizations/components/CreateOrganizationModal';
import { EditOrganizationModal } from '@frontend/features/organizations/components/EditOrganizationModal';
import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';

import { routes } from '../routes';

export const OrganizationsList = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const theme = useTheme();
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [editId, setEditId] = useState<string | undefined>();
  const [isOpenEdit, setIsOpenEdit] = useState<boolean>(false);

  const toggleCreateModal = () => setIsOpenCreate((prevState) => !prevState);
  const handleOpenEditModal = (id: string) => {
    setEditId(id);
    setIsOpenEdit(true);
  };

  const handleCloseEditModal = () => {
    setEditId(undefined);
    setIsOpenEdit(false);
  };

  const { data, isLoading } = useGetOrganizationsByAgency({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
    agencyId: Number(id),
  });

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  return (
    <ListLayout padding={0} title={t('organizations.title')} subtitle={t('organizations.subtitle')}>
      <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {data?.count} {t('organizations.results')}
        </Typography>

        <SearchInput placeholder={t('organizations.search')} onSearch={handleOnSearch} />
        <ToggleButtonGroup
          exclusive
          sx={{
            height: theme.spacing(5),
            '& button': {
              padding: theme.spacing(1),
              width: theme.spacing(5),
            },
          }}
        >
          <ToggleButton onClick={toggleCreateModal} value="add" aria-label="right aligned">
            <AddIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {id && <CreateOrganizationModal agencyId={id} open={isOpenCreate} onClose={toggleCreateModal} />}
      {id && editId && (
        <EditOrganizationModal agencyId={id} open={isOpenEdit} id={Number(editId)} onClose={handleCloseEditModal} />
      )}

      {data?.count !== undefined && data.count > 0 && (
        <Table
          height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
          columns={[
            {
              id: 'name',
              label: t('organizations.table.name'),
              align: 'left',
            },
            {
              id: 'domain',
              label: t('organizations.table.domain'),
              align: 'left',
            },
            {
              id: 'clientCount',
              label: t('organizations.table.memberCount'),
              align: 'left',
            },
            { id: 'actions', label: '', align: 'left' },
          ]}
          data={{
            key: 'id',
            items: data.items.map((item) => ({
              ...item,
              name: (
                <Link
                  to={`${routes.dashboard.agencyView.root.replace(
                    ':id',
                    String(id),
                  )}/${routes.dashboard.agencyView.organization.replace(':organizationId', String(item.id))}`}
                >
                  {item.name}
                </Link>
              ),
              clientCount: item.clientCount,
              actions: (
                <IconButton onClick={() => handleOpenEditModal(String(item.id))}>
                  <EditIcon color="primary" />
                </IconButton>
              ),
            })),
            totalCount: data.count,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}

      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
    </ListLayout>
  );
};
